import React, { useState, useEffect, useCallback } from 'react';
import * as api from '../../services/api';

interface ComponentHealth {
  name: string;
  status: 'healthy' | 'degraded' | 'down';
  latency?: number;
  details: Record<string, any>;
  checkedAt: string;
}

interface SystemHealth {
  overall: 'healthy' | 'degraded' | 'down';
  components: ComponentHealth[];
  checkedAt: string;
}

interface DeploymentInfo {
  serverVersion: string;
  nodeVersion: string;
  environment: string;
  region: string;
  uptime: number;
  gitCommit: string;
}

const statusConfig = {
  healthy: { color: '#00CED1', bg: 'bg-anchor-teal/10', border: 'border-anchor-teal/20', label: 'Healthy', dot: 'bg-anchor-teal' },
  degraded: { color: '#F59E0B', bg: 'bg-amber-500/10', border: 'border-amber-500/20', label: 'Degraded', dot: 'bg-amber-400' },
  down: { color: '#EF4444', bg: 'bg-red-500/10', border: 'border-red-500/20', label: 'Down', dot: 'bg-red-400' },
};

const HealthCard: React.FC<{ component: ComponentHealth }> = ({ component }) => {
  const cfg = statusConfig[component.status];
  return (
    <div className={`${cfg.bg} border ${cfg.border} rounded-xl p-5`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className={`w-2.5 h-2.5 rounded-full ${cfg.dot} ${component.status === 'healthy' ? '' : 'animate-pulse'}`} />
          <h4 className="text-sm font-medium text-white">{component.name}</h4>
        </div>
        <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full" style={{ color: cfg.color, background: `${cfg.color}15`, border: `1px solid ${cfg.color}30` }}>
          {cfg.label}
        </span>
      </div>
      {component.latency !== undefined && (
        <div className="text-xs text-white/40 mb-2">Latency: {component.latency}ms</div>
      )}
      <div className="space-y-1">
        {Object.entries(component.details).filter(([k]) => !k.startsWith('_')).slice(0, 6).map(([key, value]) => (
          <div key={key} className="flex justify-between text-xs">
            <span className="text-white/40">{key.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase())}</span>
            <span className="text-white/70 font-mono">{typeof value === 'object' ? JSON.stringify(value) : String(value)}</span>
          </div>
        ))}
      </div>
      <div className="mt-2 pt-2 border-t border-white/[0.04] text-[10px] text-white/25">
        Checked: {new Date(component.checkedAt).toLocaleTimeString()}
      </div>
    </div>
  );
};

interface VulnScan {
  total: number;
  critical: number;
  high: number;
  medium: number;
  low: number;
  advisories?: any[];
  scannedAt: string;
  createdBy?: string;
}

const VulnerabilityCard: React.FC<{ scan: VulnScan | null; onScan: () => void; scanning: boolean }> = ({ scan, onScan, scanning }) => {
  const hasCritical = scan && scan.critical > 0;
  const hasHigh = scan && scan.high > 0;
  const borderColor = hasCritical ? 'border-red-500/20' : hasHigh ? 'border-amber-500/20' : 'border-white/[0.06]';
  const bgColor = hasCritical ? 'bg-red-500/5' : hasHigh ? 'bg-amber-500/5' : 'bg-white/[0.02]';

  return (
    <div className={`${bgColor} border ${borderColor} rounded-xl p-5`}>
      <div className="flex items-center justify-between mb-4">
        <h4 className="text-sm font-medium text-white">Dependency Vulnerabilities</h4>
        <button
          onClick={onScan}
          disabled={scanning}
          className="text-xs text-anchor-blue hover:text-white transition-colors px-3 py-1.5 rounded border border-anchor-blue/30 hover:border-anchor-blue disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {scanning ? 'Scanning...' : 'Scan Now'}
        </button>
      </div>

      {!scan ? (
        <div className="text-sm text-white/30 text-center py-4">No scan results yet. Run a scan to check for vulnerabilities.</div>
      ) : (
        <>
          <div className="text-3xl font-bold text-white mb-3">{scan.total}</div>
          <div className="text-xs text-white/40 mb-3">Total vulnerabilities found</div>
          <div className="grid grid-cols-4 gap-2 mb-3">
            <div className="text-center">
              <div className="text-lg font-bold text-red-400">{scan.critical}</div>
              <div className="text-[10px] text-red-400/60 uppercase">Critical</div>
            </div>
            <div className="text-center">
              <div className="text-lg font-bold text-orange-400">{scan.high}</div>
              <div className="text-[10px] text-orange-400/60 uppercase">High</div>
            </div>
            <div className="text-center">
              <div className="text-lg font-bold text-yellow-400">{scan.medium}</div>
              <div className="text-[10px] text-yellow-400/60 uppercase">Medium</div>
            </div>
            <div className="text-center">
              <div className="text-lg font-bold text-white/40">{scan.low}</div>
              <div className="text-[10px] text-white/20 uppercase">Low</div>
            </div>
          </div>
          <div className="pt-2 border-t border-white/[0.04] text-[10px] text-white/25">
            Last scan: {new Date(scan.scannedAt).toLocaleString()}
            {scan.createdBy && <span> by {scan.createdBy}</span>}
          </div>
        </>
      )}
    </div>
  );
};

const InfrastructureDashboard: React.FC = () => {
  const [health, setHealth] = useState<SystemHealth | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());
  const [vulnScan, setVulnScan] = useState<VulnScan | null>(null);
  const [scanning, setScanning] = useState(false);
  const [deploymentInfo, setDeploymentInfo] = useState<DeploymentInfo | null>(null);

  const fetchDeploymentInfo = useCallback(async () => {
    try {
      const data = await api.getDeploymentInfo();
      if (data) setDeploymentInfo(data);
    } catch {
      // Non-fatal — endpoint may not be deployed yet
    }
  }, []);

  const fetchVulnScan = useCallback(async () => {
    try {
      const data = await api.getLatestVulnScan();
      if (data) setVulnScan(data);
    } catch {
      // Non-fatal — vuln scan table may not exist yet
    }
  }, []);

  const handleScanNow = useCallback(async () => {
    setScanning(true);
    try {
      const result = await api.triggerVulnScan();
      setVulnScan(result);
    } catch (err) {
      console.error('Vulnerability scan failed:', err);
    } finally {
      setScanning(false);
    }
  }, []);

  const fetchHealth = useCallback(async () => {
    try {
      const data = await api.getSystemHealth();
      setHealth(data);
      setError(null);
      setLastRefresh(new Date());
    } catch (err) {
      const errMsg = err instanceof Error ? err.message : String(err);
      setError(`Health check failed: ${errMsg}`);
      // Generate local-only health data
      setHealth({
        overall: 'degraded',
        components: [
          {
            name: 'API Backend',
            status: 'degraded',
            details: { note: 'Cannot reach API — check backend deployment' },
            checkedAt: new Date().toISOString(),
          },
          {
            name: 'Database',
            status: 'down',
            details: { note: 'Cannot verify — API unreachable' },
            checkedAt: new Date().toISOString(),
          },
          {
            name: 'Agent Fleet',
            status: 'down',
            details: { note: 'Cannot verify — API unreachable' },
            checkedAt: new Date().toISOString(),
          },
        ],
        checkedAt: new Date().toISOString(),
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchHealth();
    fetchVulnScan();
    fetchDeploymentInfo();
    const interval = setInterval(fetchHealth, 30000); // Auto-refresh every 30s
    return () => clearInterval(interval);
  }, [fetchHealth, fetchVulnScan, fetchDeploymentInfo]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-anchor-blue"></div>
      </div>
    );
  }

  const overallCfg = health ? statusConfig[health.overall] : statusConfig.down;

  return (
    <div className="space-y-6">
      {/* Overall status banner */}
      <div className={`${overallCfg.bg} border ${overallCfg.border} rounded-xl p-5 flex items-center justify-between`}>
        <div className="flex items-center gap-3">
          <div className={`w-4 h-4 rounded-full ${overallCfg.dot} ${health?.overall !== 'healthy' ? 'animate-pulse' : ''}`} />
          <div>
            <h3 className="text-lg font-medium text-white">System Status: {overallCfg.label}</h3>
            <p className="text-xs text-white/40 mt-0.5">
              {health?.components.filter(c => c.status === 'healthy').length || 0} of {health?.components.length || 0} components healthy
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-[10px] text-white/30">
            Last refresh: {lastRefresh.toLocaleTimeString()}
          </span>
          <button
            onClick={fetchHealth}
            className="text-xs text-anchor-blue hover:text-white transition-colors px-3 py-1.5 rounded border border-anchor-blue/30 hover:border-anchor-blue"
          >
            Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg px-4 py-2 text-xs text-amber-300">
          {error}
        </div>
      )}

      {/* Component health cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {health?.components.map((component) => (
          <HealthCard key={component.name} component={component} />
        ))}
      </div>

      {/* Vulnerability scan card */}
      <VulnerabilityCard scan={vulnScan} onScan={handleScanNow} scanning={scanning} />

      {/* Deployment info */}
      <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-5">
        <h3 className="text-sm font-medium text-white/50 uppercase tracking-wider mb-4">Deployment Info</h3>
        {!deploymentInfo ? (
          <div className="text-sm text-white/30 text-center py-4">Loading deployment info...</div>
        ) : (
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <div className="flex justify-between text-xs">
                <span className="text-white/40">Server Version</span>
                <span className="text-white/70 font-mono">{deploymentInfo.serverVersion}</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-white/40">Node Version</span>
                <span className="text-white/70 font-mono">{deploymentInfo.nodeVersion}</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-white/40">Environment</span>
                <span className="text-white/70 font-mono">{deploymentInfo.environment}</span>
              </div>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between text-xs">
                <span className="text-white/40">Region</span>
                <span className="text-white/70 font-mono">{deploymentInfo.region}</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-white/40">Uptime</span>
                <span className="text-white/70 font-mono">{Math.floor(deploymentInfo.uptime / 3600)}h {Math.floor((deploymentInfo.uptime % 3600) / 60)}m</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-white/40">Git Commit</span>
                <span className="text-white/70 font-mono text-[10px]">{deploymentInfo.gitCommit}</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Auto-refresh indicator */}
      <div className="text-center text-[10px] text-white/20">
        Auto-refreshing every 30 seconds
      </div>
    </div>
  );
};

export default InfrastructureDashboard;
